import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import { type TextScene } from "@folio-2026/text"
import { InteractionManager } from "three.interactive"
import * as THREE from "three/webgpu"

type RevealableItem =
  | { type: "text"; item: TextScene["texts"][number] }
  | { type: "button"; item: TextScene["smallButtons"][number] }

export default class ProjectLeaf {
  experience: Experience
  sizes: Experience["sizes"]
  textScene: TextScene
  scene: THREE.Scene
  videoEl!: HTMLVideoElement
  interactionManager: InteractionManager

  camera: THREE.PerspectiveCamera
  clickHandlerRef?: (event: unknown) => void
  isClickEnabled = true
  revealTimeoutId?: ReturnType<typeof setTimeout>
  revealTimeoutIds: ReturnType<typeof setTimeout>[] = []

  // Cache sorted reveal order (computed once since DOM order is stable)
  private static sortedRevealOrder: RevealableItem[] | null = null

  constructor({
    scene,
    textScene,
    interactionManager,
    camera,
  }: {
    scene: THREE.Scene
    textScene: TextScene
    interactionManager: InteractionManager
    camera: THREE.PerspectiveCamera
  }) {
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes

    this.scene = scene
    this.interactionManager = interactionManager
    this.textScene = textScene
    this.camera = camera

    this.addEventListeners()
  }

  private sortProjectItems() {
    // Only sort once since DOM order is stable
    if (ProjectLeaf.sortedRevealOrder !== null) {
      return
    }

    const projectTexts = this.textScene.texts.filter(
      (text) => text.name === "project",
    )
    const projectButtons = this.textScene.smallButtons.filter(
      (button) =>
        button.id === "close-project-cta" || button.id === "visit-website-cta",
    )

    // If items don't exist yet, skip sorting (will be called again when they do)
    if (projectTexts.length === 0 && projectButtons.length === 0) {
      return
    }

    // Create unified array with position info
    const items: Array<{ item: RevealableItem; position: number }> = [
      ...projectTexts.map((text) => ({
        item: { type: "text" as const, item: text },
        position: text.bounds.top,
      })),
      ...projectButtons.map((button) => ({
        item: { type: "button" as const, item: button },
        position: button.bounds.top,
      })),
    ]

    // Sort by position (top to bottom)
    items.sort((a, b) => a.position - b.position)

    ProjectLeaf.sortedRevealOrder = items.map(({ item }) => item)
  }

  addEventListeners() {
    this.experience.on(EVENTS.Loader.CurtainRemovable, () => {
      this.sortProjectItems()
    })

    this.experience.on(EVENTS.Project.Entered, this.addProjectPage.bind(this))

    this.experience.on(
      EVENTS.Project.Exiting,
      this.removeProjectPage.bind(this),
    )

    this.experience.on(EVENTS.Project.Scroll, this.manageScroll.bind(this))

    this.experience.on(
      EVENTS.Project.CloseCTA.Click,
      this.closeProjectHandler.bind(this),
    )
  }

  closeProjectHandler() {
    this.experience.navigate("/")
  }

  /**
   * Manage the horizontal scroll of the about page.
   */
  manageScroll(e: unknown) {
    const { animatedScroll } = e as {
      animatedScroll: number
    }

    this.textScene.texts.forEach((text) => {
      if (text.mesh) {
        const positionY =
          -text.bounds.top +
          animatedScroll +
          this.experience.sizes.height / 2 -
          text.bounds.height

        const positionX = text.bounds.left - this.experience.sizes.width / 2

        text.mesh.position.x = positionX
        text.mesh.position.y = positionY
      }
    })

    // Only update project buttons (close and visit website)
    const projectButtons = this.textScene.smallButtons.filter(
      (button) =>
        button.id === "close-project-cta" || button.id === "visit-website-cta",
    )

    projectButtons.forEach((button) => {
      if (button.group) {
        const y = button.bounds.top

        const positionX =
          button.bounds.left +
          button.bounds.width / 2 -
          this.experience.sizes.width / 2
        const positionY =
          -y +
          animatedScroll +
          this.experience.sizes.height / 2 -
          button.bounds.height / 2

        button.proxyMesh.position.x = positionX
        button.proxyMesh.position.y = positionY

        button.group.position.x = positionX
        button.group.position.y = positionY
      }
    })
  }

  updateTexts(projectData: (typeof projects)[number]) {
    // Build content map from projectData.content (similar to ProjectContent.tsx)
    const contentMap = new Map<string, string | string[]>()
    projectData.content.forEach((item) => {
      Object.entries(item).forEach(([key, value]) => {
        contentMap.set(key, value)
      })
    })

    // Get awards array
    const awards = projectData.content.find((item) => item.awards)?.awards
    const awardsArray = Array.isArray(awards) ? awards : []

    // Iterate through existing text instances with name "project"
    const projectTexts = this.textScene.texts.filter(
      (text) => text.name === "project",
    )

    projectTexts.forEach((textInstance) => {
      // Check if element is still connected to the DOM
      // If not, React has replaced it, so we need to re-find it
      if (!textInstance.element.isConnected) {
        // Find all project text elements
        const projectElements = Array.from(
          document.querySelectorAll(
            `[data-name="project"] [data-animation="webgl-text"]`,
          ),
        ) as HTMLElement[]

        // Find the text instance index in the projectTexts array
        const textIndex = projectTexts.indexOf(textInstance)
        if (textIndex >= 0 && textIndex < projectElements.length) {
          textInstance.element = projectElements[textIndex]
        } else {
          // Can't re-find, skip this text instance
          console.warn(
            "Could not re-find element for text instance",
            textInstance,
          )
          return
        }
      }

      // Check if the element has data-project-field attribute
      const field = textInstance.element.getAttribute("data-project-field")
      if (!field) return

      // Get the value based on the field
      let value: string = ""

      // Handle award fields (award-0, award-1, etc.)
      if (field.startsWith("award-")) {
        const awardIndex = parseInt(field.replace("award-", ""), 10)
        value = awardsArray[awardIndex] ?? ""
      }
      // Handle label fields (they are static, skip updating)
      else if (field.startsWith("label-")) {
        return // Skip label fields as they are static
      }
      // Handle button fields (skip buttons)
      else if (field === "close-cta" || field === "visit-website-cta") {
        return // Skip button fields
      }
      // Handle regular content fields
      else {
        const contentValue = contentMap.get(field)
        value = typeof contentValue === "string" ? contentValue : ""
      }

      // Update the text content
      textInstance.text = value

      // Update the geometry with new text and bounds
      textInstance.geometry.update({
        text: textInstance.text,
        width: textInstance.bounds.width,
      })

      // Update bounds in case text length changed
      textInstance.createBounds()
    })
  }

  /**
   * Add all the objects needed for the about page.
   */
  addProjectPage(event: unknown) {
    const { url } = event as { url: string }
    // Get the project data
    const projectData = projects.find((p) => p.url === url)

    if (!projectData) {
      console.warn(`Project data not found for url: ${url}`)
      return
    }

    this.updateTexts(projectData)

    // Initially position the texts
    this.manageScroll({
      animatedScroll: 0,
    })

    // Clear any existing reveal timeouts
    if (this.revealTimeoutId) {
      clearTimeout(this.revealTimeoutId)
      this.revealTimeoutId = undefined
    }
    this.revealTimeoutIds.forEach((id) => clearTimeout(id))
    this.revealTimeoutIds = []

    // Use requestAnimationFrame to ensure DOM is ready and bounds are calculated
    requestAnimationFrame(() => {
      // Force re-sort since we just created new items
      this.sortProjectItems()

      // Get project buttons to re-add them to interaction manager
      const projectButtons = this.textScene.smallButtons.filter(
        (button) =>
          button.id === "close-project-cta" ||
          button.id === "visit-website-cta",
      )

      // Re-add project buttons to interaction manager (they were removed on leave)
      projectButtons.forEach((button) => {
        if (button.proxyMesh) {
          this.interactionManager.add(button.proxyMesh)
        }
      })

      // Reveal with stagger (100ms delay between each item)
      const sortedOrder = ProjectLeaf.sortedRevealOrder

      if (!sortedOrder) {
        return
      }
      sortedOrder.forEach((item, index) => {
        const timeoutId = setTimeout(() => {
          if (item.type === "text") {
            item.item.reveal()
          } else {
            item.item.reveal()
          }
        }, index * 100)
        this.revealTimeoutIds.push(timeoutId)
      })
    })
  }

  /**
   * Remove all the objects from the about page. (geometries, materials, meshes, etc.)
   */
  async removeProjectPage() {
    // Cancel any pending reveal
    if (this.revealTimeoutId) {
      clearTimeout(this.revealTimeoutId)
      this.revealTimeoutId = undefined
    }
    this.revealTimeoutIds.forEach((id) => clearTimeout(id))
    this.revealTimeoutIds = []

    // Clear the static cache so it's fresh for the next project
    ProjectLeaf.sortedRevealOrder = null

    const projectTexts = this.textScene.texts.filter(
      (text) => text.name === "project",
    )

    // Only remove project buttons (close and visit website)
    const projectButtons = this.textScene.smallButtons.filter(
      (button) =>
        button.id === "close-project-cta" || button.id === "visit-website-cta",
    )

    const textLeavePromises = projectTexts.map((text) => text.leave())
    const smallButtonLeavePromises = projectButtons.map((button) =>
      button.leave(),
    )
    await Promise.all([...textLeavePromises, ...smallButtonLeavePromises])

    // Remove project buttons from interaction manager to prevent click interception
    projectButtons.forEach((button) => {
      if (button.proxyMesh) {
        this.interactionManager.remove(button.proxyMesh)
      }
    })
  }

  update() {}

  resize() {
    requestIdleCallback(() => {
      this.textScene.smallButtons.forEach((button) => {
        if (
          button.element.id === "close-project-cta" ||
          button.element.id === "visit-website-cta"
        ) {
          button.resize()
          this.manageScroll({
            animatedScroll: 0,
          })
        }
      })
      // Resize only the current ProjectLeaf
      // if (window.location.pathname !== this.userData.url) {
      //   return
      // }

      this.manageScroll({
        animatedScroll: 0,
      })
    })
  }
}
